<template>
    <card title="" :isFullPage="true">
        <data-table entity-name="mobile device" entity-name-plural="mobile devices" :columns="columns" :rows="rows"
            :total-row-count="totalRowCount" selection-key="mobile_id" :action-in-progress="actionsInProgress > 0"
            @queryChanged="onQueryChanged">

            <template v-slot:actions="props">
                <import-action :selection-count="props.selectionCount" :disabled="actionsInProgress > 0"
                    :entity-name="props.entityName" :entity-name-plural="props.entityNamePlural"
                    import-resource="mobile-devices"></import-action>
                <export-action v-bind="props" export-resource="mobile-devices"></export-action>
                <delete-action v-if="isSuperUser || isStaffUser || isPartner || isManager" v-bind="props" @deleteAction="onDelete"></delete-action>
                <print-action v-bind="props" @printAction="onPrint"></print-action>
            </template>
        </data-table>
    </card>
</template>
<script>
import { mapGetters } from 'vuex';
import { notify } from '../services/notify';

import api from '../services/api';
import print from '../components/DataTable/print';
import imports from '../components/DataTable/Actions/imports';
import DataTable from '../components/DataTable/DataTable.vue';
import DeleteAction from '../components/DataTable/Actions/DeleteAction.vue';
import ExportAction from '../components/DataTable/Actions/ExportAction.vue';
import ImportAction from '../components/DataTable/Actions/ImportAction.vue';
import PrintAction from '../components/DataTable/Actions/PrintAction.vue';

export default {
    name: 'MobileDevices',
    components: {
        DataTable,
        DeleteAction,
        ExportAction,
        ImportAction,
        PrintAction,
    },
    data() {
        return {
            columns: [],
            baseColumns: [
                { key: 'details', type: 'action', action: this.viewMobile, actionIcon: 'mobile', visible: true },
                { key: 'created_at', type: 'datetime', visible: true },
                { key: 'updated_at', type: 'datetime' },
                { key: 'uploaded_at', type: 'datetime' },
                { key: 'software_version' },
                { key: 'report_id', label: 'Report ID' },
                { key: 'batch_name' },

                { key: 'platform', visible: true },
                { key: 'vendor', visible: true },
                { key: 'model', visible: true },
                { key: 'model_code', visible: true },
                { key: 'serial', visible: true },
                { key: 'color' },
                { key: 'international_mobile_equipment_identity1', label: 'IMEI 1', visible: true },
                { key: 'international_mobile_equipment_identity2', label: 'IMEI 2', visible: false },
                { key: 'international_mobile_equipment_identity3', label: 'IMEI 3', visible: false },
                { key: 'international_mobile_equipment_identity4', label: 'IMEI 4', visible: false },
                { key: 'mobile_equipment_identifier1', label: 'MEID 1', visible: true },
                { key: 'mobile_equipment_identifier2', label: 'MEID 2', visible: false },
                { key: 'mobile_equipment_identifier3', label: 'MEID 3', visible: false },
                { key: 'mobile_equipment_identifier4', label: 'MEID 4', visible: false },
                { key: 'internal_storage_capacity_display', proxy_key: 'internal_storage_capacity_bytes', label: 'Capacity', type: 'number', visible: false,
                    units: [
                        { label: 'MB', fromUser: v => v * 1e+6, toUser: v => v / 1e+6 },
                        { label: 'GB', fromUser: v => v * 1e+9, toUser: v => v / 1e+9 },
                        { label: 'TB', fromUser: v => v * 1e+12, toUser: v => v / 1e+12 },
                    ]
                },
                { key: 'battery_health' },
                {
                    key: 'erase_algorithm_display', label: 'Erase method', proxy_key: "erase_algorithm", type: 'category',
                    categories: [
                        "factory_reset",
                    ],
                },
                {
                    key: 'erase_result_display', label: 'Erase result', visible: false, type: 'category',
                    categories: ['Not erased', 'Pass', 'Fail', 'Canceled']
                },
                { key: 'erase_start_time', label: 'Erase start', type: 'datetime' },
                { key: 'erase_finish_time', label: 'Erase finish', type: 'datetime' },

                {
                    key: 'extra', type: 'table', columns: [
                        { key: 'key' },
                        { key: 'value' }
                    ], visible: true
                },
                {
                    key: 'log', type: 'table', columns: [
                        { key: 'level', formatter: (value) => ['DEBUG', 'INFO', 'WARNING', 'ERROR'][value] },
                        { key: 'message' }
                    ], visible: true
                }
            ],
            rows: [],
            totalRowCount: 0,
            lastQuery: null,
            actionsInProgress: 0,
            savedTableName: null,
        }
    },
    async created() {
        await this.$store.cache.dispatch('user/fetchMe');
        if (this.isSuperUser || this.isStaffUser || this.isPartner) {
            this.columns = [{ key: 'organization.name', label: 'Organization', visible: true }, { key: 'group.name', label: 'Group', visible: true }, ...this.baseColumns];
        }
        else if (this.isManager) {
            this.columns = [{ key: 'group.name', label: 'Group', visible: true }, ...this.baseColumns];
        }
        else {
            this.columns = this.baseColumns;
        }
    },
    methods: {
        async refreshQuery() {
            if (this.lastQuery !== null) {
                await this.onQueryChanged(this.lastQuery);
            }
        },
        async onQueryChanged(query) {
            this.lastQuery = query;
            try {
                const response = await api.getWithParams('/mobile-devices/', query);
                this.rows = response.data.data;
                this.totalRowCount = response.data.pagination.count;
            }
            catch (err) {
                this.rows = [];
                this.totalRowCount = 0;
                notify().error('Error fetching reports.');
            }
        },
        async onDelete(selectOrFilter) {
            this.actionsInProgress++;
            try {
                const response = await api.delete('/mobile-devices/', selectOrFilter);
                await this.refreshQuery();
            }
            catch (err) {
                notify().error('Error deleting reports.');
            }
            finally {
                this.actionsInProgress--;
            }
        },
        async onPrint(selectOrFilter, rowConverter) {
            this.actionsInProgress++;
            try {
                const response = await api.postJSON('/mobile-devices/select', selectOrFilter);
                const rows = response.data;
                await print.pdf(rowConverter(rows));
            }
            catch (err) {
                notify().error('Error printing a report.');
            }
            finally {
                this.actionsInProgress--;
            }
        },
        async viewMobile(row) {
            this.$router.push(`/mobile-devices/${row.mobile_id}`);
        },
    },
    computed: {
        ...mapGetters('user', ['isSuperUser', 'isStaffUser', 'isPartner', 'isManager']),
    },
}
</script>
